import { useMemo } from 'react';
import { createWithEqualityFn as create } from 'zustand/traditional';
import type { DiscountDto, MenuSectionDto } from '../../generated-api';
import type { DiscountDto as OsDiscountDto } from '@ordr-as/ordr-service-types/epos';
import { useMenuSectionsStore } from './MenuSectionsStore';
import { getAllItemsFromSections } from '../utils/cart';

function mapToDiscountV3(raw: OsDiscountDto): DiscountDto {
  return {
    amount: raw.amount,
    discountCodes: raw.discountCodes.filter((dc) => dc.active && !dc.deleted).map((dc) => dc.discountCode),
    id: raw.discountId,
    allSectionsSelected: raw.allSectionsSelected ?? false,
    name: raw.name,
    productIds: [], // Frontend computes from sections
    sectionIds: raw.sectionIds,
    type: raw.type,
  };
}

function mapDiscountsForV3(rawDiscounts: OsDiscountDto[]): DiscountDto[] {
  return rawDiscounts.filter((d) => d.active && !d.deleted).map(mapToDiscountV3);
}

interface DiscountActions {
  setDiscounts: (discounts: OsDiscountDto[], locationId: string) => void;
  clear: () => void;
}

export interface DiscountState {
  discounts: DiscountDto[];
  locationId: string | undefined;
  actions: DiscountActions;
}

export const useDiscountStore = create<DiscountState>()((set) => ({
  actions: {
    clear: () => set({ discounts: [], locationId: undefined }),
    setDiscounts: (osDiscounts, locationId) => set({ discounts: mapDiscountsForV3(osDiscounts), locationId }),
  },
  discounts: [],
  locationId: undefined,
}));

export const useDiscountActions = () => useDiscountStore((state) => state.actions);

/**
 * Compute productIds for a discount based on menu sections.
 * Mirrors the backend DiscountMapper logic that was previously done server-side.
 *
 * @see backend/.../mappers/DiscountMapper.kt
 */
function computeProductIds(discount: DiscountDto, sections: MenuSectionDto[]): string[] {
  // Compute from sections (V3+ behavior)
  const eligibleSections = discount.allSectionsSelected
    ? sections
    : sections.filter((s) => discount.sectionIds.includes(s.sectionId));

  const eligibleItems = getAllItemsFromSections(eligibleSections);

  const itemIds = eligibleItems.map((item) => item.variantLinkId);

  const optionGroupIds = eligibleItems
    .flatMap((item) => item.optionGroups)
    .filter((og) => og.variantLinkId)
    .map((og) => og.variantLinkId!);

  // Use Set to deduplicate
  return [...new Set([...itemIds, ...optionGroupIds])];
}

/**
 * Returns discounts with computed productIds based on current menu sections.
 * Reactively updates when either discounts or sections change.
 */
export const useDiscounts = (): DiscountDto[] => {
  const discounts = useDiscountStore((state) => state.discounts);
  const sections = useMenuSectionsStore((state) => state.sections);

  return useMemo(
    () =>
      discounts.map((discount) => ({
        ...discount,
        productIds: computeProductIds(discount, sections),
      })),
    [discounts, sections]
  );
};
